var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { useDebouncedValue, useLoader } from 'ziphy-web-shared/basic/hooks';
import { isGroupedDropdownItems } from './Dropdown.helpers';
export function useDropdown(initialData = [], { initialSearchString = '', onSearch, onSearchRender, searchValuesPaths = [], } = {}) {
    const isAsyncItems = typeof onSearchRender === 'function' && typeof onSearch === 'function';
    const dropdownRef = useRef(null);
    const totalItems = useRef(0);
    const totalFilteredItems = useRef(0);
    const [_searchString, setSearchString] = useState(initialSearchString);
    const searchString = useDebouncedValue(_searchString, isAsyncItems ? 300 : 0);
    const [result, setResult] = useState(null);
    const [items, setItems] = useState(initialData);
    useEffect(() => {
        if (!isAsyncItems && !_.isEqual(initialData, items)) {
            setItems(initialData);
        }
    }, [initialData]);
    const { isLoading, reload } = useLoader(() => __awaiter(this, void 0, void 0, function* () {
        if (!isAsyncItems)
            return;
        const result = yield onSearch(searchString, dropdownRef.current);
        setResult(result);
    }), [searchString, onSearch]);
    useEffect(() => {
        if (result && typeof onSearchRender === 'function') {
            setItems(onSearchRender(result));
        }
    }, [onSearchRender, result]);
    const normalizedItems = useMemo(() => {
        if ((items === null || items === void 0 ? void 0 : items.length) === 0)
            return [];
        return 'items' in items[0]
            ? items
            : [{ items: items }];
    }, [items]);
    const filteredItems = useMemo(() => {
        totalItems.current = 0;
        totalFilteredItems.current = 0;
        const needle = searchString.trim().toLowerCase().split(' ');
        let filteredList = normalizedItems;
        if (isAsyncItems) {
            const totalCount = filteredList.reduce((sum, item) => sum + item.items.length, 0);
            totalFilteredItems.current = totalCount;
            totalItems.current = totalCount;
        }
        else if (needle.length) {
            filteredList = filteredList.map((item) => (Object.assign(Object.assign({}, item), { items: item.items.filter((item) => {
                    const values = searchValuesPaths.reduce((acc, path) => {
                        if (typeof path === 'function') {
                            acc.push(path(item));
                        }
                        else {
                            const value = _.get(item, path, '');
                            Array.isArray(value) ? (acc = acc.concat(value)) : acc.push(value);
                        }
                        return acc;
                    }, []);
                    const preparedString = [item.label, item.description, ...values].join(' ').toLowerCase();
                    const result = needle.every((n) => preparedString.includes(n));
                    if (result)
                        totalFilteredItems.current++;
                    totalItems.current++;
                    return result;
                }) })));
        }
        return filteredList.filter((item) => item.items.length > 0);
    }, [normalizedItems, searchString]);
    return {
        dropdownRef,
        isLoading,
        searchString,
        setSearchString,
        normalizedItems,
        filteredItems,
        totalItems: totalItems.current,
        totalFilteredItems: totalFilteredItems.current,
        reloadAsyncItems: reload,
    };
}
export const useDropdownFocusedIndex = (items, otherRef, isActive) => {
    const [index, setIndex] = useState({
        group: -1,
        item: -1,
        isOtherFocused: false,
    });
    useEffect(() => {
        var _a;
        if (items.length === 0)
            return;
        const isGrouped = isGroupedDropdownItems(items);
        const hasOther = !!((_a = otherRef.current) === null || _a === void 0 ? void 0 : _a.originalFieldRef.current);
        const otherElement = hasOther ? otherRef.current.originalFieldRef.current : null;
        const isLastElement = (index) => isGrouped
            ? index.group === items.length - 1 && index.item === items[index.group].items.length - 1
            : index.item === items.length - 1;
        const prevGroup = (index, items) => {
            if (index.group <= 0) {
                index.group = items.length - 1;
            }
            else {
                index.group -= 1;
            }
        };
        const nextGroup = (index, items) => {
            if (index.group >= items.length - 1) {
                index.group = 0;
            }
            else {
                index.group += 1;
            }
        };
        const onArrowUp = () => {
            const result = Object.assign(Object.assign({}, index), { isOtherFocused: false });
            if (hasOther && result.group <= 0 && result.item <= 0) {
                if (isGrouped) {
                    result.group = items.length - 1;
                    result.item = items[result.group].items.length;
                }
                else {
                    result.item = items.length;
                }
                result.isOtherFocused = true;
            }
            else if (isGrouped && result.item <= 0) {
                prevGroup(result, items);
                result.item = items[result.group].items.length - 1;
            }
            else if (!isGrouped && result.item <= 0) {
                result.item = items.length - 1;
            }
            else if (result.item > 0) {
                result.item -= 1;
            }
            setIndex(result);
        };
        const onArrowDown = () => {
            const result = Object.assign(Object.assign({}, index), { isOtherFocused: false });
            if (isGrouped && result.group < 0) {
                result.group = 0;
                result.item = 0;
            }
            else if (hasOther && isLastElement(result)) {
                result.item += 1;
                result.isOtherFocused = true;
            }
            else if (isGrouped && result.item >= items[result.group].items.length - 1) {
                nextGroup(result, items);
                result.item = 0;
            }
            else if (!isGrouped && result.item >= items.length - 1) {
                result.item = 0;
            }
            else if (isGrouped && result.item < items[result.group].items.length) {
                result.item += 1;
            }
            else if (!isGrouped && result.item < items.length) {
                result.item += 1;
            }
            setIndex(result);
        };
        const onKeydown = (e) => {
            if (e.key === 'ArrowUp') {
                onArrowUp();
            }
            else if (e.key === 'ArrowDown') {
                onArrowDown();
            }
        };
        const onFocusOther = () => {
            setIndex((prevState) => {
                const result = Object.assign(Object.assign({}, prevState), { isOtherFocused: true });
                if (isGrouped) {
                    result.group = items.length - 1;
                    result.item = items[result.group].items.length;
                }
                else {
                    result.item = items.length;
                }
                if (result.item === prevState.item &&
                    result.group === prevState.group &&
                    result.isOtherFocused === prevState.isOtherFocused) {
                    return prevState;
                }
                else {
                    return result;
                }
            });
        };
        if (isActive) {
            window.addEventListener('keydown', onKeydown);
            if (hasOther) {
                otherElement.addEventListener('focus', onFocusOther);
            }
        }
        return () => {
            window.removeEventListener('keydown', onKeydown);
            if (hasOther) {
                otherElement.removeEventListener('focus', onFocusOther);
            }
        };
    }, [items, index, isActive]);
    return index;
};
